'use client'

import * as React from 'react'
import { useRouter } from 'next/navigation'
import { useTranslations } from 'next-intl'
import {
  BellIcon,
  BellOffIcon,
  BellRingIcon,
  CircleCheckIcon,
  CirclePauseIcon,
  CircleXIcon,
  CoinsIcon,
  ListChecksIcon,
  MessageSquareIcon,
  TriangleAlertIcon,
  WalletIcon,
  XIcon,
  type LucideIcon,
} from 'lucide-react'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Spinner } from '@/components/ui/spinner'
import { useApiQuery } from '@/hooks/use-api'
import { useNotificationUnreadCount } from '@/hooks/use-notification-poll'
import { useNotificationToasts } from '@/hooks/use-notification-toasts'
import { swrFetcher } from '@/hooks/api-fetcher'
import { useOptionalCurrentBrand } from '@/hooks/use-current-brand'
import { buildBrandUrl, buildOrgUrl } from '@/lib/routes'
import { useAuthContext } from '@/hooks/use-auth-context'
import { useOrgAdminGate } from '@/hooks/use-org-access'
import { useSWRConfig } from 'swr'
import { cn } from '@/lib/utils'
import { parseServerDate } from '@/lib/server-date'

function listUrlFor(brandSlug: string | null): string {
  return brandSlug
    ? `/api/notifications?brand=${encodeURIComponent(brandSlug)}`
    : '/api/notifications'
}

type NotificationType =
  | 'automation_run_done'
  | 'automation_run_error'
  | 'action_review_ready'
  | 'agent_turn_complete'
  | 'out_of_credits'
  | 'ad_review_alert'
  | 'chatgpt_ads_low_balance'
  | 'chatgpt_ads_balance_depleted'
  | 'chatgpt_ads_balance_recovered'
  | 'chatgpt_ads_monthly_statement'

type NotificationItem = {
  id: string
  type: NotificationType
  titleKey: string
  titleParams: Record<string, unknown> | null
  payload: Record<string, unknown> | null
  readAt: string | null
  createdAt: string
}

type NotificationListPage = {
  items: NotificationItem[]
  nextBefore: string | null
}

const TONE_CLASS = {
  neutral: 'text-muted-foreground',
  success: 'text-green-600 dark:text-green-500',
  warning: 'text-amber-600 dark:text-amber-500',
  danger: 'text-red-600 dark:text-red-500',
} as const

type NotificationIcon = { icon: LucideIcon; tone: keyof typeof TONE_CLASS }

// Partial so a type the backend ships before this client learns it still
// renders, with the generic bell.
const TYPE_ICON: Partial<Record<NotificationType, NotificationIcon>> = {
  automation_run_done: { icon: CircleCheckIcon, tone: 'success' },
  automation_run_error: { icon: CircleXIcon, tone: 'danger' },
  action_review_ready: { icon: ListChecksIcon, tone: 'neutral' },
  agent_turn_complete: { icon: MessageSquareIcon, tone: 'neutral' },
  out_of_credits: { icon: CoinsIcon, tone: 'warning' },
  ad_review_alert: { icon: TriangleAlertIcon, tone: 'warning' },
  chatgpt_ads_low_balance: { icon: WalletIcon, tone: 'warning' },
  chatgpt_ads_balance_depleted: { icon: CirclePauseIcon, tone: 'danger' },
  chatgpt_ads_balance_recovered: { icon: CircleCheckIcon, tone: 'success' },
  chatgpt_ads_monthly_statement: { icon: WalletIcon, tone: 'neutral' },
}

const FALLBACK_ICON: NotificationIcon = { icon: BellIcon, tone: 'neutral' }

function relativeTime(
  t: ReturnType<typeof useTranslations<'Notifications.relativeTime'>>,
  iso: string,
): string {
  const then = parseServerDate(iso).getTime()
  const now = Date.now()
  const diffSeconds = Math.max(0, Math.floor((now - then) / 1000))
  if (diffSeconds < 60) return t('justNow')
  const minutes = Math.floor(diffSeconds / 60)
  if (minutes < 60) return t('minutesAgo', { count: minutes })
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return t('hoursAgo', { count: hours })
  const days = Math.floor(hours / 24)
  return t('daysAgo', { count: days })
}

function deepLinkFor(
  item: NotificationItem,
  /**
   * Whether the reader may open the org Billing page. The `out_of_credits`
   * row is the one destination inside the admin-only Organization Workspace,
   * so for a member the row stays (they still need to know credits ran out)
   * but stops being a link into a 404.
   */
  orgWorkspaceVisible: boolean,
): string | null {
  if (item.type === 'chatgpt_ads_monthly_statement') return null
  const payload = item.payload ?? {}
  // The conversation / action-review routes live under the brand-scoped
  // layout `/o/{orgSlug}/b/{brandSlug}/chat/{id}`. Both `org_slug` and
  // `brand_slug` are captured at notification create time so the click
  // navigates straight to the right brand workspace, regardless of the
  // recipient's currently active Clerk session.
  const brandSlug = payload['brand_slug']
  const orgSlug = payload['org_slug']
  if (
    item.type === 'out_of_credits' ||
    item.type === 'chatgpt_ads_low_balance' ||
    item.type === 'chatgpt_ads_balance_depleted' ||
    item.type === 'chatgpt_ads_balance_recovered'
  ) {
    // Org-level destination: only `org_slug` is required — the brand may be
    // deleted (or absent for org-level triggers) and the billing page is
    // org-scoped. Mirrors deep_link.py's org-only branch (dual-source sync).
    return orgWorkspaceVisible && typeof orgSlug === 'string'
      ? buildOrgUrl(orgSlug, 'billing')
      : null
  }
  if (typeof brandSlug !== 'string' || typeof orgSlug !== 'string') {
    // Pre-migration notifications may lack `org_slug` — disable the
    // deep link rather than synthesize a broken URL.
    return null
  }
  switch (item.type) {
    case 'automation_run_done': {
      // Success — jump straight to the conversation so the user sees
      // what the agent produced.
      const conversationId = payload['conversation_id']
      if (typeof conversationId !== 'string') return null
      return buildBrandUrl(orgSlug, brandSlug, `chat/${conversationId}`)
    }
    case 'automation_run_error':
      // Failure — jump to the brand's automations listing where the
      // user can see run history / status / retry controls.
      return buildBrandUrl(orgSlug, brandSlug, 'automations')
    case 'action_review_ready': {
      const chatId = payload['chat_id']
      if (typeof chatId !== 'string') return null
      return buildBrandUrl(orgSlug, brandSlug, `chat/${chatId}`)
    }
    case 'agent_turn_complete': {
      const conversationId = payload['conversation_id']
      if (typeof conversationId !== 'string') return null
      return buildBrandUrl(orgSlug, brandSlug, `chat/${conversationId}`)
    }
    case 'ad_review_alert':
      // There is no object-level ads review route; use the ads dashboard.
      return buildBrandUrl(orgSlug, brandSlug, 'dashboards')
  }
}

function NotificationRow({
  item,
  onClick,
}: {
  item: NotificationItem
  onClick: (item: NotificationItem) => void
}) {
  const titleT = useTranslations('Notifications.title')
  const timeT = useTranslations('Notifications.relativeTime')
  const unread = item.readAt === null

  // ``titleParams`` is JSON the backend wrote — values are interpolated
  // by next-intl. The catalog is the single source of truth for the
  // placeholder names; the runtime call is dynamic so the next-intl
  // typed wrapper is bypassed via a loose cast.
  const params = (item.titleParams ?? {}) as Record<string, string | number>
  // Trim the namespace prefix the backend stores so the t() lookup
  // matches the message-catalog key directly.
  const key = item.titleKey.replace(/^notifications\.title\./, '')
  const looseT = titleT as unknown as (
    k: string,
    p: Record<string, string | number>,
  ) => string
  const title = looseT(key, params)
  const { icon: Icon, tone } = TYPE_ICON[item.type] ?? FALLBACK_ICON

  return (
    <button
      type="button"
      onClick={() => onClick(item)}
      className="hover:bg-muted/60 focus-visible:bg-muted/60 flex w-full items-start gap-3 px-4 py-3 text-start text-sm transition-colors outline-none"
    >
      <span
        aria-hidden
        className="bg-muted flex size-8 shrink-0 items-center justify-center rounded-lg"
      >
        <Icon className={cn('size-4', TONE_CLASS[tone])} />
      </span>
      <span className="flex min-w-0 flex-1 flex-col gap-1">
        {/* Titles embed vendor object names with no spaces (e.g. ad names
            joined by underscores); break them rather than widen the dialog. */}
        <span className="leading-snug wrap-anywhere">{title}</span>
        {item.type === 'chatgpt_ads_monthly_statement' &&
        typeof item.payload?.statement_text === 'string' ? (
          <span className="font-mono text-xs wrap-anywhere whitespace-pre-wrap">
            {item.payload.statement_text}
          </span>
        ) : null}
        <span className="text-muted-foreground text-xs">
          {relativeTime(timeT, item.createdAt)}
        </span>
      </span>
      {unread ? (
        <span
          aria-hidden
          className="bg-primary mt-1.5 size-2 shrink-0 rounded-full"
        />
      ) : null}
    </button>
  )
}

type NotificationsDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
}

/**
 * The notification feed, opened from the account menu (see `NavUser`).
 *
 * Always mounted next to that menu — not only while open — because it also
 * hosts the 15 s poll that raises toasts and browser notifications. Only the
 * list query is gated on `open`.
 */
export function NotificationsDialog({
  open,
  onOpenChange,
}: NotificationsDialogProps) {
  const t = useTranslations('Notifications.bell')
  const router = useRouter()
  const { getToken } = useAuthContext()
  const { mutate } = useSWRConfig()
  // Scope everything to the active brand. The account menu can render
  // outside the brand layout (e.g. top-level settings during transition
  // states), so the brand context is optional — when absent, the feed
  // shows the legacy unscoped view.
  const brand = useOptionalCurrentBrand()
  const brandSlug = brand?.brandSlug ?? null
  const listUrl = listUrlFor(brandSlug)
  const markAllUrl = brandSlug
    ? `/api/notifications/mark-all-read?brand=${encodeURIComponent(brandSlug)}`
    : '/api/notifications/mark-all-read'
  const { refresh } = useNotificationUnreadCount()
  // Gates the one deep link that points into the admin-only org workspace.
  const orgWorkspaceVisible = useOrgAdminGate()

  // The list query is gated on dialog open: SWR only fires when a key
  // is non-null, so the user pays the cost of the list fetch only when
  // they actually open the popover.
  const { data, isLoading } = useApiQuery<NotificationListPage>(
    open ? listUrl : null,
    { revalidateOnFocus: false, keepPreviousData: true },
  )

  // Mark-all-read fires once each time the dialog opens. The endpoint
  // is idempotent so a duplicate open is a no-op when there's nothing
  // new. Refresh the badge after to flush the local cache.
  //
  // Read brand-derived URLs through a ref so a brand switch mid-popover
  // doesn't re-trigger the effect (Bugbot PR #1718) — the original POST
  // for the brand the user opened the popover under should be the only
  // one. Subsequent brand navigation closes the sidebar surface anyway.
  const urlsRef = React.useRef({ listUrl, markAllUrl })
  React.useEffect(() => {
    urlsRef.current = { listUrl, markAllUrl }
  }, [listUrl, markAllUrl])
  React.useEffect(() => {
    if (!open) return
    const { listUrl: currentListUrl, markAllUrl: currentMarkAllUrl } =
      urlsRef.current
    let cancelled = false
    void (async () => {
      try {
        await swrFetcher<{ marked: number }>(
          currentMarkAllUrl,
          { method: 'POST' },
          getToken,
        )
      } catch {
        // best-effort; the bell count will eventually correct via polling
      }
      if (!cancelled) {
        await mutate(currentListUrl)
        await refresh()
      }
    })()
    return () => {
      cancelled = true
    }
  }, [open, getToken, mutate, refresh])

  const handleItemClick = React.useCallback(
    (item: NotificationItem) => {
      onOpenChange(false)
      const href = deepLinkFor(item, orgWorkspaceVisible)
      if (href) router.push(href)
    },
    [router, onOpenChange, orgWorkspaceVisible],
  )

  // Toast + (optional) browser system notification for any new row
  // arriving via the 15 s poll. Reuses the same deep-link logic on
  // click so a toast and a dialog row navigate the same way.
  useNotificationToasts({ onItemClick: handleItemClick })

  // Browser notification permission gating. The Notification API only
  // grants on a user gesture, so we surface a one-click prompt in the
  // dialog header when permission is still ``default``. ``denied`` is
  // respected — we never re-prompt; the user can re-grant in browser
  // settings.
  const [permission, setPermission] = React.useState<
    NotificationPermission | 'unsupported'
  >('unsupported')

  React.useEffect(() => {
    if (typeof window === 'undefined' || !('Notification' in window)) return
    setPermission(window.Notification.permission)
  }, [])

  const contentRef = React.useRef<HTMLDivElement>(null)

  const requestBrowserPermission = React.useCallback(async () => {
    if (typeof window === 'undefined' || !('Notification' in window)) return
    const result = await window.Notification.requestPermission()
    setPermission(result)
  }, [])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        ref={contentRef}
        aria-describedby={undefined}
        showCloseButton={false}
        // Land focus on the dialog itself, not its first button: otherwise
        // "Enable browser notifications" wears a focus ring on every open.
        // The dialog carries the focus ring instead, so a keyboard user still
        // sees where focus went.
        onOpenAutoFocus={event => {
          event.preventDefault()
          contentRef.current?.focus()
        }}
        // The min height keeps an empty or one-row dialog from collapsing;
        // min() lets both caps give way on short viewports, where the list
        // (min-h-0) shrinks and scrolls instead of being clipped.
        className="focus-visible:ring-ring/50 flex max-h-[min(40rem,85dvh)] min-h-[min(17.5rem,85dvh)] w-[min(26rem,calc(100vw-2rem))] max-w-[calc(100vw-2rem)] flex-col gap-0 overflow-hidden p-0 outline-none focus-visible:ring-[3px]"
      >
        <DialogHeader className="flex-row items-center gap-2 border-b py-2.5 ps-4 pe-2.5">
          <DialogTitle className="flex-1 text-sm font-medium">
            {t('header')}
          </DialogTitle>
          {permission === 'default' ? (
            // Shrinks and truncates on narrow phones so the close button
            // beside it is never pushed out of the dialog.
            <Button
              variant="ghost"
              size="xs"
              onClick={requestBrowserPermission}
              className="text-muted-foreground min-w-0 shrink"
            >
              <BellRingIcon />
              <span className="truncate">{t('enableBrowser')}</span>
            </Button>
          ) : null}
          <DialogClose asChild>
            <Button variant="ghost" size="icon-sm">
              <XIcon />
              <span className="sr-only">Close</span>
            </Button>
          </DialogClose>
        </DialogHeader>
        <div className="flex min-h-0 flex-1 flex-col divide-y overflow-x-hidden overflow-y-auto">
          {isLoading && !data ? (
            <div className="text-muted-foreground flex flex-1 items-center justify-center p-6">
              <Spinner />
            </div>
          ) : data && data.items.length > 0 ? (
            data.items.map(item => (
              <NotificationRow
                key={item.id}
                item={item}
                onClick={handleItemClick}
              />
            ))
          ) : (
            <div className="text-muted-foreground flex flex-1 flex-col items-center justify-center gap-2 p-6 text-center text-sm">
              <BellOffIcon className="size-5" />
              {t('empty')}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
